import { useState } from "react";
import { MagnifyingGlass, Spinner } from "@phosphor-icons/react";

const CATEGORY_SUGGESTIONS = [
    "Plumbers",
    "Electricians",
    "Builders",
    "Painters",
    "Locksmiths",
    "Roofing",
    "Gardening",
    "Cleaners",
    "Landscapers",
    "Concrete",
    "Glaziers",
];

const LOCATION_SUGGESTIONS = [
    "Auckland",
    "Manukau",
    "North Shore",
    "Hamilton",
    "Wellington",
    "Christchurch",
    "Tauranga",
    "Dunedin",
    "Palmerston North",
    "Napier",
    "Queenstown",
];

export default function SearchForm({ onSubmit, isRunning }) {
    const [category, setCategory] = useState("Plumbers");
    const [location, setLocation] = useState("Auckland");
    const [source, setSource] = useState("all");
    const [maxResults, setMaxResults] = useState("");
    const [forceRefresh, setForceRefresh] = useState(false);

    const submit = (e) => {
        e?.preventDefault?.();
        if (!category.trim() || !location.trim()) return;
        const raw = String(maxResults).trim();
        // Blank / "all" / "0" = collect as many as possible (server hard cap 5000)
        const parsed = raw === "" || raw.toLowerCase() === "all" ? 5000 : Number(raw);
        const clamped = Math.max(1, Math.min(5000, Number.isFinite(parsed) ? parsed : 5000));
        onSubmit({
            category: category.trim(),
            location: location.trim(),
            source,
            max_results: clamped,
            force_refresh: forceRefresh,
        });
    };

    return (
        <form className="panel" onSubmit={submit} data-testid="search-form">
            <div className="panel-header">
                <span className="label-kicker">Search Parameters</span>
                <span className="mono text-xs text-[#52525b]">
                    {source === "all"
                        ? "yellow + finda + localist + web"
                        : source === "directories"
                        ? "yellow + finda + localist"
                        : source}
                </span>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="flex flex-col gap-2">
                    <span className="label-kicker">Service Category</span>
                    <input
                        list="cat-list"
                        type="text"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        placeholder="Plumbers"
                        className="brutal-input px-3 py-2.5 text-sm"
                        data-testid="category-input"
                        required
                    />
                    <datalist id="cat-list">
                        {CATEGORY_SUGGESTIONS.map((c) => (
                            <option key={c} value={c} />
                        ))}
                    </datalist>
                </label>

                <label className="flex flex-col gap-2">
                    <span className="label-kicker">Location (NZ)</span>
                    <input
                        list="loc-list"
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="Auckland"
                        className="brutal-input px-3 py-2.5 text-sm"
                        data-testid="location-input"
                        required
                    />
                    <datalist id="loc-list">
                        {LOCATION_SUGGESTIONS.map((c) => (
                            <option key={c} value={c} />
                        ))}
                    </datalist>
                </label>

                <label className="flex flex-col gap-2">
                    <span className="label-kicker">Source</span>
                    <select
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        className="brutal-input px-3 py-2.5 text-sm"
                        data-testid="source-select"
                    >
                        <option value="all">All sources (yellow + finda + localist + web)</option>
                        <option value="directories">All directories (yellow + finda + localist)</option>
                        <option value="yellow">yellow.co.nz only</option>
                        <option value="finda">finda.co.nz only</option>
                        <option value="localist">localist.co.nz only</option>
                        <option value="google">Web search only</option>
                        <option value="both">Legacy (yellow + web)</option>
                    </select>
                </label>

                <label className="flex flex-col gap-2">
                    <span className="label-kicker">Max Results</span>
                    <input
                        type="text"
                        inputMode="numeric"
                        placeholder="All available"
                        value={maxResults}
                        onChange={(e) => setMaxResults(e.target.value)}
                        className="brutal-input px-3 py-2.5 text-sm mono"
                        data-testid="max-results-input"
                    />
                    <span className="text-[10px] text-[#52525b] mono">
                        Leave blank for no limit (may take several minutes)
                    </span>
                </label>
            </div>
            <div className="border-t border-[#e4e4e7] px-5 py-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-4 flex-wrap">
                    <label
                        className="flex items-center gap-2 cursor-pointer select-none"
                        htmlFor="force-refresh"
                        data-testid="force-refresh-label"
                    >
                        <span
                            className="relative inline-flex items-center"
                            style={{ width: 32, height: 18 }}
                        >
                            <input
                                id="force-refresh"
                                type="checkbox"
                                className="sr-only"
                                checked={forceRefresh}
                                onChange={(e) => setForceRefresh(e.target.checked)}
                                data-testid="force-refresh-toggle"
                            />
                            <span
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    background: forceRefresh ? "#002FA7" : "#e4e4e7",
                                    transition: "background 120ms",
                                }}
                            />
                            <span
                                style={{
                                    position: "absolute",
                                    top: 2,
                                    left: forceRefresh ? 16 : 2,
                                    width: 14,
                                    height: 14,
                                    background: "#fff",
                                    transition: "left 120ms",
                                }}
                            />
                        </span>
                        <span className="text-xs text-[#52525b]">
                            Force refresh provider websites (ignore crawl cache)
                        </span>
                    </label>
                </div>
                <button
                    type="submit"
                    className="btn-primary"
                    disabled={!category.trim() || !location.trim()}
                    data-testid="start-search-btn"
                >
                    {isRunning ? (
                        <>
                            <Spinner size={16} weight="bold" className="animate-spin" />
                            Queue new search
                        </>
                    ) : (
                        <>
                            <MagnifyingGlass size={16} weight="bold" />
                            Start search
                        </>
                    )}
                </button>
            </div>
        </form>
    );
}
